import axios from "axios";

export type ClaimBlockReason =
  | "ofac"
  | "already_claimed"
  | "commitment_exceeded"
  | "cancelled"
  | "sender_blocked"
  | "expired"
  | "sender_waiting"
  | "sender_failed"
  | "recipient_review"
  | "generic";

export const isOriginatorClosedReason = (
  reason: ClaimBlockReason
): boolean => reason === "cancelled" || reason === "sender_blocked";

export interface ParsedBindError {
  reason: ClaimBlockReason;
  code?: string;
}

/** Maps widget bind API errors to a stable UI reason. */
export const parseBindError = (err: unknown): ParsedBindError => {
  if (!axios.isAxiosError(err)) {
    return { reason: "generic" };
  }

  const code = err.response?.data?.error?.code as string | undefined;

  switch (code) {
    case "OFAC_HIT":
      return { reason: "ofac", code };
    case "PAYOUT_ALREADY_BOUND":
      return { reason: "already_claimed", code };
    case "SEND_COMMITMENT_EXCEEDED":
    case "QUOTE_MISMATCH":
      return { reason: "commitment_exceeded", code };
    case "DEFERRED_PAYOUT_CANCELLED":
      return { reason: "cancelled", code };
    case "SENDER_BLOCKED":
      return { reason: "sender_blocked", code };
    case "DEFERRED_PAYOUT_EXPIRED":
    case "LINK_EXPIRED":
      return { reason: "expired", code };
    case "SENDER_IDENTITY_PENDING":
      return { reason: "sender_waiting", code };
    case "SENDER_IDENTITY_FAILED":
      return { reason: "sender_failed", code };
    default:
      return { reason: "generic", code };
  }
};

export const claimBlockTitleKey = (reason: ClaimBlockReason): string => {
  switch (reason) {
    case "ofac":
      return "claimBlocked.ofacTitle";
    case "already_claimed":
      return "claimBlocked.alreadyClaimedTitle";
    case "commitment_exceeded":
      return "claimBlocked.commitmentTitle";
    case "cancelled":
    case "sender_blocked":
      return "claimBlocked.cancelledTitle";
    case "expired":
      return "claimBlocked.expiredTitle";
    case "sender_waiting":
      return "claimBlocked.senderWaitingTitle";
    case "sender_failed":
      return "claimBlocked.senderFailedTitle";
    case "recipient_review":
      return "claimBlocked.recipientReviewTitle";
    default:
      return "error.title";
  }
};

export const claimBlockMessageKey = (reason: ClaimBlockReason): string => {
  switch (reason) {
    case "ofac":
      return "claimBlocked.ofacMessage";
    case "already_claimed":
      return "claimBlocked.alreadyClaimedMessage";
    case "commitment_exceeded":
      return "claimBlocked.commitmentMessage";
    case "cancelled":
    case "sender_blocked":
      return "claimBlocked.cancelledMessage";
    case "expired":
      return "claimBlocked.expiredMessage";
    case "sender_waiting":
      return "claimBlocked.senderWaitingMessage";
    case "sender_failed":
      return "claimBlocked.senderFailedMessage";
    case "recipient_review":
      return "claimBlocked.recipientReviewMessage";
    default:
      return "error.tryAgainLater";
  }
};
